<template>
    <div id="customer-accueil">

        <h1 class="visually-hidden">Accueil - PharmaConnect</h1>

        <!-- Ligne 1 : Bienvenue + Filtre période (6 / 6) -->
        <b-row class="match-height">
            <b-col cols="12" md="6" class="mb-2">
                <b-card no-body class="welcome-banner h-100 mb-0">
                    <div class="welcome-banner__content">
                        <div>
                            <h2 class="text-white mb-25 font-weight-bolder">
                                Bienvenue<span v-if="welcomeName">, {{ welcomeName }}</span> 👋
                            </h2>
                            <p class="text-white-75 mb-1">
                                Retrouvez vos répartitions, arrivages, nouveautés et promotions du moment.
                            </p>
                            <b-button variant="light" class="font-weight-bolder welcome-banner__cta"
                                :to="{ name: 'new-order' }">
                                <feather-icon icon="PlusIcon" /> Nouvelle commande
                            </b-button>
                        </div>
                        <feather-icon icon="ShoppingBagIcon" class="welcome-banner__icon d-none d-md-block" size="90" />
                    </div>
                </b-card>
            </b-col>
            <b-col cols="12" md="6" class="mb-2">
                <b-card no-body class="h-100 mb-0">
                    <b-card-body class="d-flex align-items-center">
                        <b-row class="align-items-end w-100">
                            <b-col cols="6" md="4" class="mb-1 mb-md-0">
                                <label class="font-weight-bold mb-25">Du</label>
                                <b-form-datepicker v-model="startDate" :max="endDate" locale="fr"
                                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                                    placeholder="jj/mm/aaaa" />

                            </b-col>
                            <b-col cols="6" md="4" class="mb-1 mb-md-0">
                                <label class="font-weight-bold mb-25">Au</label>
                                <b-form-datepicker v-model="endDate" :min="startDate" locale="fr"
                                    :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                                    placeholder="jj/mm/aaaa" />

                            </b-col>
                            <b-col cols="12" md="4">
                                <b-button block variant="primary" @click="loadData">
                                    <feather-icon icon="SearchIcon" /> Appliquer
                                </b-button>
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <!-- Popup quantité (ajout à la commande courante) -->
        <add-to-cart-modal ref="addToCartModal" />

        <!-- Popup "Voir tout" : réutilise les grilles existantes -->
        <b-modal ref="showAllModal" :title="showAllTitle" size="xl" hide-footer scrollable lazy>
            <Distribution v-if="showAllCategory === 'distribution'" @itemClicked="onDetails" @addToCart="onAdd" />
            <Arrival v-else-if="showAllCategory === 'arrival'" @itemClicked="onDetails" @addToCart="onAdd" />
            <Novelty v-else-if="showAllCategory === 'novelty'" @itemClicked="onDetails" @addToCart="onAdd" />
            <Promotion v-else-if="showAllCategory === 'promotion'" @itemClicked="onDetails" @addToCart="onAdd" />
        </b-modal>

        <!-- Popup détails article -->
        <b-modal ref="item-modal" title="Détails de l'article" size="xl" hide-footer scrollable>
            <ProductDetailsPARA v-if="selectedProduct.type == 'PARA'" :product="selectedProduct" />
            <ProductDetailsMED v-else :product="selectedProduct" />
        </b-modal>

        <b-overlay :show="showLoading" spinner-variant="primary">

            <!-- Indicateurs -->
            <b-row>
                <b-col v-for="(c, i) in cards" :key="i" cols="6" md="4" lg class="mb-2">
                    <b-card no-body class="h-100 mb-0">
                        <b-card-body class="d-flex justify-content-between align-items-center">
                            <div class="truncate">
                                <h2 class="mb-25 font-weight-bolder">{{ c.value }}</h2>
                                <span class="d-block">{{ c.title }}</span>
                                <small v-if="c.sub" class="text-muted">{{ c.sub }}</small>
                            </div>
                            <b-avatar :variant="`light-${c.color}`" size="45">
                                <feather-icon size="21" :icon="c.icon" />
                            </b-avatar>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>

        </b-overlay>

        <!-- Actualités : top 5 par catégorie -->
        <b-row class="match-height">
            <b-col v-for="cat in categoryCards" :key="cat.key" cols="12" md="6" xl="3" class="mb-2">
                <category-preview-card :title="cat.title" :icon="cat.icon" :variant="cat.variant" :items="cat.top"
                    :total-count="cat.total" :loading="cat.loading" :metric-field="cat.metricField" :promo="cat.promo"
                    @add="onAdd" @details="onDetails" @show-all="onShowAll(cat.key)" />
            </b-col>
        </b-row>

        <!-- Liens utiles -->
        <b-row>
            <b-col cols="12">
                <b-card title="Liens utiles">
                    <b-button class="mr-1 mb-1" variant="outline-danger"
                        href="https://www.youtube.com/watch?v=XoWQ9oJA7kE" target="_blank" rel="noopener noreferrer">
                        <feather-icon icon="PlayCircleIcon" /> Plateforme TEJ
                    </b-button>
                    <b-button class="mr-1 mb-1" variant="outline-primary"
                        href="http://www.phct.com.tn/index.php/communiques/circulaires-de-la-pct" target="_blank"
                        rel="noopener noreferrer">
                        <feather-icon icon="FileTextIcon" /> Circulaires de la PCT
                    </b-button>
                    <b-button class="mr-1 mb-1" variant="outline-warning"
                        href="http://www.phct.com.tn/index.php/communiques/retraits-du-marche" target="_blank"
                        rel="noopener noreferrer">
                        <feather-icon icon="AlertTriangleIcon" /> Retraits du marché
                    </b-button>
                    <b-button class="mr-1 mb-1" variant="outline-info"
                        href="http://www.phct.com.tn/index.php/communiques/appels-a-la-concurrence" target="_blank"
                        rel="noopener noreferrer">
                        <feather-icon icon="BriefcaseIcon" /> Appels à la concurrence
                    </b-button>
                </b-card>
            </b-col>
        </b-row>

        <!-- Raccourcis -->
        <b-row>
            <b-col cols="12">
                <b-card title="Raccourcis">
                    <b-button class="mr-1 mb-1" variant="gradient-success" :to="{ name: 'new-order' }">
                        <feather-icon icon="PlusIcon" /> Nouvelle commande
                    </b-button>
                    <b-button class="mr-1 mb-1" variant="gradient-primary" :to="{ name: 'catalog' }">
                        <feather-icon icon="ImageIcon" /> Catalogue
                    </b-button>
                    <b-button class="mr-1 mb-1" variant="gradient-info" :to="{ name: 'docs-invoices' }">
                        <feather-icon icon="FileMinusIcon" /> Mes factures
                    </b-button>
                    <b-button class="mr-1 mb-1" variant="gradient-warning" :to="{ name: 'claims' }">
                        <feather-icon icon="MessageSquareIcon" /> Réclamations
                    </b-button>
                </b-card>
            </b-col>
        </b-row>

        <!-- Synchronisation -->
        <b-row>
            <b-col cols="12">
                <b-card no-body>
                    <b-card-body>
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <h5 class="mb-25">
                                    <feather-icon icon="RefreshCwIcon" class="mr-50" /> Synchronisation des articles
                                </h5>
                                <p class="text-muted mb-25 small">
                                    Dernière mise à jour : <strong>{{ lastSyncDate }}</strong>
                                </p>
                                <p class="text-muted mb-0 small">
                                    Articles en base locale : <strong>{{ itemCount }}</strong>
                                </p>
                            </div>
                            <b-button variant="outline-primary" size="sm" :disabled="syncing" @click="forceSyncItems">
                                <feather-icon icon="RefreshCwIcon" class="mr-50" />
                                {{ syncing ? 'En cours...' : 'Forcer la synchronisation' }}
                            </b-button>
                        </div>
                        <b-progress v-if="syncing" :value="syncProgress" max="100" variant="primary" class="mt-1"
                            show-progress animated height="12px" />
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>

import * as itemDB from '@/indexDB/item.js';
import CategoryPreviewCard from './accueil/CategoryPreviewCard.vue';
import AddToCartModal from './accueil/AddToCartModal.vue';
import Arrival from './order/Arrival.vue';
import Novelty from './order/Novelty.vue';
import Promotion from './order/Promotion.vue';
import Distribution from './order/Distribution.vue';
import ProductDetailsMED from '@/views/product/ProductDetailsMED.vue';
import ProductDetailsPARA from '@/views/product/ProductDetailsPARA.vue';

// French amount formatting: thousands grouping + 3 decimals (millimes), e.g. "51 370,457 TND"
const fmtAmount = (v, currency = true) => {
    const s = Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    return currency ? `${s} TND` : s;
};

export default {

    components: {
        CategoryPreviewCard,
        AddToCartModal,
        Arrival,
        Novelty,
        Promotion,
        Distribution,
        ProductDetailsMED,
        ProductDetailsPARA,
    },

    metaInfo() {
        return {
            title: "Accueil - PharmaConnect",
            meta: [
                { vmid: "description", name: "description", content: "Tableau de bord PharmaConnect : suivez vos livraisons (BL), factures, commandes, retours et réclamations." },
                { name: "robots", content: "index, follow" },
            ],
        };
    },

    data() {
        return {
            showLoading: false,
            syncing: false,
            syncProgress: 0,
            lastSyncDate: localStorage.getItem('lastItemUpdate') || 'Jamais',
            itemCount: 0,
            // --- Actualités (top 5 par catégorie) ---
            actualites: {
                distribution: { items: [], loading: true },
                arrival: { items: [], loading: true },
                novelty: { items: [], loading: true },
                promotion: { items: [], loading: true },
            },
            showAllCategory: null,
            selectedProduct: {},
            startDate: '',
            endDate: '',
            kpi: {
                bl: 0,
                blAmount: 0,
                invoices: 0,
                invoicesAmount: 0,
                orders: 0,
                returns: 0,
                claims: 0,
            },
            recentOrders: [],
            recentFields: [
                { key: 'Posting_Date', label: 'Date', formatter: v => (v ? v.substring(0, 10) : '') },
                { key: 'No', label: 'N°' },
                { key: 'Amount', label: 'Montant', formatter: v => fmtAmount(v, false) },
                { key: 'Order_Status', label: 'Statut' },
            ],
        };
    },
    computed: {
        cards() {
            return [
                { title: 'Total BL', value: this.kpi.bl, sub: fmtAmount(this.kpi.blAmount), icon: 'TruckIcon', color: 'primary' },
                { title: 'Factures', value: this.kpi.invoices, sub: fmtAmount(this.kpi.invoicesAmount), icon: 'FileTextIcon', color: 'success' },
                { title: 'Commandes', value: this.kpi.orders, sub: '', icon: 'ShoppingCartIcon', color: 'info' },
                { title: 'Retours', value: this.kpi.returns, sub: '', icon: 'CornerUpLeftIcon', color: 'warning' },
                { title: 'Réclamations', value: this.kpi.claims, sub: '', icon: 'MessageSquareIcon', color: 'danger' },
            ];
        },
        categoryCards() {
            const a = this.actualites;
            return [
                { key: 'distribution', title: 'Répartition', icon: 'GridIcon', variant: 'info', metricField: 'Open_Quantity', promo: false, items: a.distribution.items, loading: a.distribution.loading },
                { key: 'arrival', title: 'Arrivage', icon: 'TruckIcon', variant: 'warning', metricField: 'Sales_Inventory', promo: false, items: a.arrival.items, loading: a.arrival.loading },
                { key: 'novelty', title: 'Nouveautés', icon: 'StarIcon', variant: 'success', metricField: '', promo: false, items: a.novelty.items, loading: a.novelty.loading },
                { key: 'promotion', title: 'Promotions', icon: 'GiftIcon', variant: 'danger', metricField: '', promo: true, items: a.promotion.items, loading: a.promotion.loading },
            ].map(c => ({ ...c, top: c.items.slice(0, 5), total: c.items.length }));
        },
        showAllTitle() {
            const map = {
                distribution: 'Articles en répartition',
                arrival: 'Liste des arrivages',
                novelty: 'Liste des nouveautés',
                promotion: 'Liste des promotions',
            };
            return map[this.showAllCategory] || '';
        },
        // Real customer name, same source as the navbar (app-userdata store), not the login code.
        welcomeName() {
            const u = (this.$store.state['app-userdata'] && this.$store.state['app-userdata'].AppActiveUser) || {};
            if (u.firstName) {
                return u.lastName ? `${u.firstName} ${u.lastName}` : u.firstName;
            }
            return u.fullName || '';
        },
    },
    created() {
        const today = new Date();
        const past = new Date();
        past.setDate(past.getDate() - 30);
        this.endDate = today.toISOString().split('T')[0];
        this.startDate = past.toISOString().split('T')[0];
        this.loadData();
        this.loadActualites();
        this.loadItemCount();
    },
    methods: {
        // Date range forwarded to the backend; the backend adds the Sell_to_Customer_No scope.
        // gt (start-1) / lt (end+1) to make the range inclusive, same convention as the docs pages.
        generateDateFilters() {
            const s = new Date(this.startDate); s.setDate(s.getDate() - 1);
            const e = new Date(this.endDate); e.setDate(e.getDate() + 1);
            return [
                { field: 'Posting_Date', value: s.toISOString().split('T')[0], operator: 'gt' },
                { field: 'Posting_Date', value: e.toISOString().split('T')[0], operator: 'lt' },
            ];
        },
        async loadData() {
            this.showLoading = true;
            await Promise.all([
                this.loadBL(),
                this.loadInvoices(),
                this.loadOrders(),
                this.loadReturns(),
                this.loadClaims(),
            ]);
            this.showLoading = false;
        },
        async loadBL() {
            try {
                const res = await this.$http.post('shipment_header/findWhere', this.generateDateFilters());
                const rows = (Array.isArray(res.data) ? res.data : []).filter(x => x.No && x.No.substring(0, 2) !== 'RV');
                this.kpi.bl = rows.length;
                this.kpi.blAmount = rows.reduce((s, x) => s + (Number(x.Amount_Including_VAT) || 0), 0);
            } catch (e) {
                this.kpi.bl = 0;
                this.kpi.blAmount = 0;
            }
        },
        async loadInvoices() {
            try {
                // Same two sources as the "Mes Factures" page (Facturevente + posted invoices SI), merged.
                const [inv, vinv] = await Promise.all([
                    this.$http.post('invoice_header/findWhere', this.generateDateFilters()),
                    this.$http.post('validated_invoice_header/findWhere', this.generateDateFilters()),
                ]);
                const rows = [
                    ...(Array.isArray(inv.data) ? inv.data : []),
                    ...(Array.isArray(vinv.data) ? vinv.data : []),
                ].filter(x => x.No && !x.No.startsWith('RV'));
                this.kpi.invoices = rows.length;
                this.kpi.invoicesAmount = rows.reduce((s, x) => s + (Number(x.Amount_Including_VAT) || 0), 0);
            } catch (e) {
                this.kpi.invoices = 0;
                this.kpi.invoicesAmount = 0;
            }
        },
        async loadOrders() {
            try {
                const res = await this.$http.post('order_header/findWhere', this.generateDateFilters());
                let rows = (Array.isArray(res.data) ? res.data : []).filter(x => x.No && x.No.substring(0, 2) !== 'RV');
                this.kpi.orders = rows.length;
                this.recentOrders = [...rows]
                    .sort((a, b) => (b.Posting_Date || '').localeCompare(a.Posting_Date || ''))
                    .slice(0, 10);
            } catch (e) {
                this.kpi.orders = 0;
                this.recentOrders = [];
            }
        },
        async loadReturns() {
            try {
                const res = await this.$http.post('return_header/findWhere', this.generateDateFilters());
                this.kpi.returns = Array.isArray(res.data) ? res.data.length : 0;
            } catch (e) {
                this.kpi.returns = 0;
            }
        },
        async loadClaims() {
            try {
                const res = await this.$http.get('claim');
                const rows = Array.isArray(res.data) ? res.data : [];
                this.kpi.claims = rows.filter(c => {
                    const d = c.Date ? c.Date.substring(0, 10) : '';
                    return d && d >= this.startDate && d <= this.endDate;
                }).length;
            } catch (e) {
                this.kpi.claims = 0;
            }
        },

        // ---------- Actualités (top 5 par catégorie) ----------
        // Same promotion window/filter as Promotion.vue (last month → today, on Creation_Date).
        promotionDateFilters() {
            const start = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 2);
            const end = new Date();
            start.setDate(start.getDate() - 1);
            end.setDate(end.getDate() + 1);
            return [
                { field: 'Creation_Date', value: start.toISOString().split('T')[0] + 'T00:00:00Z', operator: 'gt' },
                { field: 'Creation_Date', value: end.toISOString().split('T')[0] + 'T00:00:00Z', operator: 'lt' },
            ];
        },
        async loadActualites() {
            await Promise.all([
                this.fetchCategory('distribution', () => this.$http.get('distribution')),
                this.fetchCategory('arrival', () => this.$http.get('arrival'), rows => rows.filter(i => i.Sales_Inventory >= 0)),
                this.fetchCategory('novelty', () => this.$http.get('novelty')),
                this.fetchCategory('promotion', () => this.$http.post('promotion/findWhere', this.promotionDateFilters()),
                    rows => rows.filter(i => i.Inventory >= (i.Free_Sales_Qty + i.Sales_threshold))),
            ]);
        },
        async fetchCategory(key, request, transform) {
            this.actualites[key].loading = true;
            try {
                const res = await request();
                let rows = Array.isArray(res.data) ? res.data : [];
                if (transform) rows = transform(rows);
                this.actualites[key].items = rows;
            } catch (e) {
                this.actualites[key].items = [];
            } finally {
                this.actualites[key].loading = false;
            }
        },
        onAdd(item) {
            this.$refs.addToCartModal.open(item);
        },
        onShowAll(key) {
            this.showAllCategory = key;
            this.$refs.showAllModal.show();
        },
        async onDetails(data) {
            const no = (data && data.No != undefined) ? data.No : (data && data.data ? data.data.No : undefined);
            if (!no) return;
            try {
                const resp = await this.$http.get('_item/getItemCodeByItemNo/' + no);
                if (resp.data != '') {
                    const item = await this.$store.dispatch('app-items/GET_ITEM_BY_CODE_HYDRATED', resp.data);
                    if (!item) {
                        this.$swal({
                            title: 'Article Actuellement Indisponible',
                            text: "Cet article n'existe pas dans le catalogue.",
                            icon: 'warning',
                            customClass: { confirmButton: 'btn btn-primary' },
                            buttonsStyling: false,
                        });
                        return;
                    }
                    this.selectedProduct = item;
                    this.$refs['item-modal'].show();
                } else {
                    this.$swal({
                        title: 'Article Actuellement Indisponible',
                        text: "Cet article n'existe pas dans le catalogue.",
                        icon: 'warning',
                        customClass: { confirmButton: 'btn btn-primary' },
                        buttonsStyling: false,
                    });
                }
            } catch (e) {
                // silencieux
            }
        },

        async loadItemCount() {
            await itemDB.openDatabase();
            const med = await itemDB.getItemCount(itemDB.ITEM_MED_NAME);
            const para = await itemDB.getItemCount(itemDB.ITEM_PARA_NAME);
            itemDB.closeDatabase();
            this.itemCount = med + para;
        },

        async forceSyncItems() {
            this.syncing = true;
            this.syncProgress = 0;
            await itemDB.openDatabase();
            await itemDB.clearStore(itemDB.ITEM_MED_NAME);
            await itemDB.clearStore(itemDB.ITEM_PARA_NAME);
            itemDB.closeDatabase();
            localStorage.removeItem('lastItemUpdate');
            localStorage.removeItem('lastBlockedSync');
            await this.$store.dispatch('app-items/FETCH_ITEMS_FROM_BACKEND_WITH_PERCENTAGE', (pct) => {
                this.syncProgress = Math.floor(pct);
            });
            this.lastSyncDate = localStorage.getItem('lastItemUpdate') || 'Jamais';
            await this.loadItemCount();
            this.syncing = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.welcome-banner {
    background: linear-gradient(118deg, #7367f0, rgba(115, 103, 240, 0.7));
    box-shadow: 0 4px 18px -4px rgba(115, 103, 240, 0.65);
    border: 0;
}

.welcome-banner__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 1.75rem;
}

.welcome-banner__icon {
    color: rgba(255, 255, 255, 0.35);
}

.text-white-75 {
    color: rgba(255, 255, 255, 0.8) !important;
}

/* "Nouvelle commande" CTA — bright white with bold purple text + shadow so it stands out on the gradient */
.welcome-banner__cta {
    color: #5e50ee !important;
    background-color: #fff !important;
    border-color: #fff !important;
    box-shadow: 0 6px 16px -2px rgba(0, 0, 0, 0.3);
    transition: transform .15s ease, box-shadow .15s ease;
}

.welcome-banner__cta:hover,
.welcome-banner__cta:focus {
    color: #5e50ee !important;
    background-color: #fff !important;
    transform: translateY(-2px);
    box-shadow: 0 10px 22px -2px rgba(0, 0, 0, 0.4);
}

.video-embed {
    position: relative;
    width: 100%;
    padding-top: 56.25%; // 16:9
}

.video-embed iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: 0.428rem;
}

@media (max-width: 768px) {
    .card {
        background-color: initial;
        box-shadow: initial;
    }
}
</style>
